import { useQuery } from '@tanstack/react-query';
import {
  IncidentsSafetyDashboardQuery,
  IncidentsSafetyDashboardQueryVariables,
  ObservationsSafetyDashboardQuery,
  ObservationsSafetyDashboardQueryVariables,
  OpenIncidentSafetyDashboardQuery,
  OpenIncidentSafetyDashboardQueryVariables,
} from '__generated__/graphql';
import {
  INCIDENT_SAFETY_DASHBOARD_QUERY,
  OPEN_INCIDENT_SAFETY_DASHBOARD_QUERY,
} from 'containers/safety-overview/cards/incidents/queries';
import { OBSERVATIONS_SAFETY_DASHBOARD_QUERY } from 'containers/safety-overview/cards/observations/graphql/queries';
import { useOrganization } from 'hooks';
import useSafetyIncidentsEnabled from 'hooks/feature/safety/useSafetyIncidentsEnabled/useSafetyIncidentsEnabled';
import useSafetyObservationsEnabled from 'hooks/feature/safety/useSafetyObservationsEnabled/useSafetyObservationsEnabled';
import useGraphQLPaging from 'hooks/graphql/useGraphQLPaging/useGraphQLPaging';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';

export interface IPendingSafetyTasksAggregatedInfo {
  openObservations: number;
  openIncidents: number;
  incidentsNeedingReview: number;
}

export default function usePendingSafetyTasksData() {
  const pageAll = useGraphQLPaging();
  const baseKey = useReactQueryBaseKey();
  const organization = useOrganization();
  const isSafetyIncidentsEnabled = useSafetyIncidentsEnabled();
  const isSafetyObservationsEnabled = useSafetyObservationsEnabled();

  const { data: openObservations } = useQuery({
    queryKey: [baseKey, 'observation', 'pending-safety-tasks'],
    queryFn: async () => {
      return pageAll<ObservationsSafetyDashboardQuery, ObservationsSafetyDashboardQueryVariables>(
        {
          document: OBSERVATIONS_SAFETY_DASHBOARD_QUERY,
          variables: {
            first: 500,
            filter: {
              deletedOn: { isNull: true },
              answers: {
                processedOn: { isNull: true },
              },
            },
          },
        },
        'observations'
      );
    },
    enabled: isSafetyObservationsEnabled,
  });

  const { data: openIncidents } = useQuery({
    queryKey: [
      baseKey,
      'safety-signature',
      'pending-safety-tasks',
      { isSafetySignaturesEnabled: organization.safetySignature },
    ],
    queryFn: async () => {
      return pageAll<OpenIncidentSafetyDashboardQuery, OpenIncidentSafetyDashboardQueryVariables>(
        {
          document: OPEN_INCIDENT_SAFETY_DASHBOARD_QUERY,
          variables: {
            first: 500,
            filter: {
              deletedOn: { isNull: true },
              injured: { equal: true },
              injuredResolvedOn: { isNull: true },
            },
          },
        },
        'safetySignatures'
      );
    },
    enabled: organization.safetySignature,
  });

  const { data: incidentsNeedingReview } = useQuery({
    queryKey: [baseKey, 'incidents', 'pending-safety-tasks'],
    queryFn: async () => {
      return pageAll<IncidentsSafetyDashboardQuery, IncidentsSafetyDashboardQueryVariables>(
        {
          document: INCIDENT_SAFETY_DASHBOARD_QUERY,
          variables: {
            first: 500,
            filter: {
              reviewedOn: { isNull: true },
              deletedOn: { isNull: true },
            },
          },
        },
        'incidents'
      );
    },
    enabled: isSafetyIncidentsEnabled,
  });

  return {
    openObservations: openObservations?.length,
    openIncidents: openIncidents?.length,
    incidentsNeedingReview: incidentsNeedingReview?.length,
  };
}
